import { formatDate } from '@/helpers';
import { Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertApartmentPropertyUse,
  convertBuildingStandard,
  convertInspectionType,
  convertInternalWall,
  convertOccupancyStatus,
  convertPreservationState,
  convertSolarOrientation,
  convertUnitPosition,
  convertViewQuality,
} from '../../../api/characteristics/enumerations';
import {
  ApartmentEvaluatingUnit,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { AnswerTag, AnswerTagsBox, StyledDivider } from '../styles';

interface ApartmentEvaluatingUnitSectionProps {
  formData: ApartmentEvaluatingUnit;
  sectionStatus?: SectionStatus;
}

export const ApartmentEvaluatingUnitSection = memo(
  function ApartmentEvaluatingUnitSection({
    formData,
    sectionStatus,
  }: ApartmentEvaluatingUnitSectionProps) {
    return (
      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <SectionStatusBadge status={sectionStatus} />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-vistoria"
          >
            Vistoria
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Vistoria
              </Typography>
              <Typography>
                {formData.inspection.inspection_type
                  ? convertInspectionType(formData.inspection.inspection_type)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Data da Vistoria
              </Typography>
              <Typography>
                {formData.inspection.inspection_date
                  ? formatDate(formData.inspection.inspection_date)
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-caracteristicas-gerais"
          >
            Características Gerais
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Andar
              </Typography>
              <Typography>
                {formData.general_characteristics.floor ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Qtd. de Pavimentos na Unidade
              </Typography>
              <Typography>
                {formData.general_characteristics.unit_floors_quantity ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                N° da Unidade no Andar
              </Typography>
              <Typography>
                {formData.general_characteristics.unit_number_on_floor ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Unidade Cobertura
              </Typography>
              <Typography>
                {formData.general_characteristics.is_penthouse == null
                  ? '-'
                  : String(
                      formData.general_characteristics.is_penthouse
                        ? 'Sim'
                        : 'Não'
                    )}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Estado de Conservação
              </Typography>
              <Typography>
                {formData.general_characteristics.preservation_state
                  ? convertPreservationState(
                      formData.general_characteristics.preservation_state
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Padrão de Acabamento
              </Typography>
              <Typography>
                {formData.general_characteristics.building_standard
                  ? convertBuildingStandard(
                      formData.general_characteristics.building_standard
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Destinação
              </Typography>
              <Typography>
                {formData.general_characteristics.property_use
                  ? convertApartmentPropertyUse(
                      formData.general_characteristics.property_use
                    )
                  : '-'}
              </Typography>
            </Grid>
            {formData.general_characteristics.property_use_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Destinação - Outros
                </Typography>
                <Typography>
                  {formData.general_characteristics.property_use_other}
                </Typography>
              </Grid>
            )}
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Ocupação
              </Typography>
              <Typography>
                {formData.general_characteristics.occupancy_status
                  ? convertOccupancyStatus(
                      formData.general_characteristics.occupancy_status
                    )
                  : '-'}
              </Typography>
            </Grid>
            {formData.general_characteristics.occupancy_status_other && (
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Ocupação - Outros
                </Typography>
                <Typography>
                  {formData.general_characteristics.occupancy_status_other}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography fontWeight={600}>Paredes Internas</Typography>
        </Grid>
        <Grid item xs={12}>
          <AnswerTagsBox>
            {formData.general_characteristics.internal_wall.length > 0 ? (
              formData.general_characteristics.internal_wall.map((v) => (
                <AnswerTag key={v}>{convertInternalWall(v)}</AnswerTag>
              ))
            ) : (
              <Typography>-</Typography>
            )}
            {formData.general_characteristics.internal_wall_other && (
              <AnswerTag>
                {formData.general_characteristics.internal_wall_other}
              </AnswerTag>
            )}
          </AnswerTagsBox>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-posicao-unidade"
          >
            Posição da Unidade
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Posição Física
              </Typography>
              <Typography>
                {formData.position_and_insertion.unit_position
                  ? convertUnitPosition(
                      formData.position_and_insertion.unit_position
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Orientação Solar
              </Typography>
              <Typography>
                {formData.position_and_insertion.solar_orientation
                  ? convertSolarOrientation(
                      formData.position_and_insertion.solar_orientation
                    )
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Vista
              </Typography>
              <Typography>
                {formData.position_and_insertion.view_quality
                  ? convertViewQuality(
                      formData.position_and_insertion.view_quality
                    )
                  : '-'}
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid item xs={12}>
          <Typography
            fontSize={18}
            fontWeight={600}
            id="evaluating-unit-vagas-garagem"
          >
            Vagas de Garagem
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cobertas Livres/Independentes
              </Typography>
              <Typography>
                {formData.parking_spaces.covered_free ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Cobertas Bloqueadas/Bloqueantes
              </Typography>
              <Typography>
                {formData.parking_spaces.covered_blocked ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Descobertas Livres/Independentes
              </Typography>
              <Typography>
                {formData.parking_spaces.uncovered_free ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Descobertas Bloqueadas/Bloqueantes
              </Typography>
              <Typography>
                {formData.parking_spaces.uncovered_blocked ?? '-'}
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Total de Vagas
              </Typography>
              <Typography>{formData.parking_spaces.total ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);
